import deepmerge from "./deepmerge.helper";

/**
 * Configuration types for field type mapping
 */
interface FieldConfig {
  dateFields: string[];
  booleanFields: string[];
  numericFields: string[];
}

/**
 * Type for the structured filter object returned by the parser
 */
type ParsedFilter = {
  [key: string]: any;
  orderBy?: Record<string, "asc" | "desc">;
  OR?: Record<string, any>[];
};

/**
 * Default configuration for field types
 */
const DEFAULT_FIELD_CONFIG: FieldConfig = {
  dateFields: ["createdAt", "updatedAt", "deletedAt", "date"],
  booleanFields: ["isActive", "isDeleted", "isPublished", "isArchived"],
  numericFields: ["age", "price", "quantity", "amount", "rating"],
};

/**
 * Parses query parameters into a structured filter object compatible with Prisma queries.
 * Supports various operators and data type conversions with deep merge strategy.
 *
 * @param query - Object containing query parameters
 * @param fieldConfig - Optional configuration for field type mapping
 * @returns Structured filter object for database queries
 *
 * @example
 * // Basic usage
 * parseQueryParamsWithModifiers({ name__contains: 'john' })
 * // => { name: { contains: 'john', mode: 'sensitive' } }
 *
 * @example
 * // Deep merge example
 * parseQueryParamsWithModifiers({
 *   'some__data': '1',
 *   'some__info': '2'
 * })
 * // => { some: { data: '1', info: '2' } }
 *
 * @example
 * // Complex query
 * parseQueryParamsWithModifiers({
 *   name__not__equals: 'john',
 *   age__gt: '25',
 *   tags__in: 'tag1,tag2',
 *   orderBy__createdAt: 'desc'
 * })
 */
export function parseQueryParamsWithModifiers(
  query: Record<string, any>,
  fieldConfig: FieldConfig = DEFAULT_FIELD_CONFIG
): ParsedFilter {
  const entries = Object.entries(JSON.parse(JSON.stringify(query)));
  let result: ParsedFilter = {};

  for (const [key, value] of entries) {
    const parts = key.split("__");
    if (!value && value !== false && value !== "false" && parts.length < 2)
      continue;

    const stringValue = Array.isArray(value) ? value[0]?.toString() : value;

    let currentResult: any = {};

    if (parts.length < 2) {
      currentResult[key] =
        typeof value === "string" && !Number.isNaN(value)
          ? convertValue(stringValue, parts[0], fieldConfig)
          : value;
    } else {
      const fieldName = parts[0];

      if (fieldName === "orderBy" && parts.length === 2) {
        currentResult.orderBy = {};
        currentResult.orderBy[parts[1]] = stringValue as "asc" | "desc";
      } else if (parts.length === 1) {
        currentResult[fieldName] = {
          equals: convertValue(stringValue, fieldName, fieldConfig),
        };
      } else {
        let nestedObj: any = {};
        let currentLevel = nestedObj;
        let currentKey = fieldName;

        for (let i = 1; i < parts.length - 1; i++) {
          currentLevel[currentKey] = {};
          currentLevel = currentLevel[currentKey];
          currentKey = parts[i];
        }

        const lastOperator = parts[parts.length - 1];

        switch (lastOperator) {
          case "icontains":
            currentLevel[currentKey] = {
              contains: stringValue,
              mode: "insensitive",
            };
            break;

          case "contains":
            currentLevel[currentKey] = {
              contains: stringValue,
              mode: "sensitive",
            };
            break;

          case "in":
          case "notIn":
            currentLevel[currentKey] = {
              [lastOperator]: stringValue
                .split(",")
                .map((v: string) =>
                  convertValue(v.trim(), fieldName, fieldConfig)
                ),
            };
            break;

          case "or":
            const values: string[] = stringValue.split(",");
            nestedObj.OR = values.map((val) => ({
              [fieldName]: {
                equals: convertValue(val.trim(), fieldName, fieldConfig),
              },
            }));
            break;

          case "isNull":
            currentLevel[currentKey] = {
              equals: stringValue.toLowerCase() === "true" ? null : undefined,
            };
            break;

          case "isEmpty":
            currentLevel[currentKey] = {
              equals: stringValue.toLowerCase() === "true" ? "" : undefined,
            };
            break;

          default:
            currentLevel[currentKey] = {
              [lastOperator]: convertValue(stringValue, fieldName, fieldConfig),
            };
        }

        currentResult = nestedObj;
      }
    }

    // Deep merge the current result with the accumulated result
    result = deepmerge(result, currentResult);
  }

  return result;
}

/**
 * Converts string values to appropriate types based on field configuration
 *
 * @example
 * // Example usage:
 * const query = {
 *   name__not__equals: 'uanela',
 *   email__contains: 'example.com',
 *   description__icontains: 'test',
 *   age__gt: '25',
 *   status: 'active',
 *   tags__in: 'tag1,tag2,tag3',
 *   createdAt__gt: '2024-01-01',
 *   isActive: 'true',
 *   orderBy__createdAt: 'desc',
 *   some__data: '1',
 *   some__info: '2'
 * };
 *
 * const result = parseQueryParamsWithModifiers(query);
 * // Result will properly merge nested objects instead of overwriting
 */
function convertValue(
  value: string,
  fieldName: string,
  config: FieldConfig
): any {
  // Handle date fields
  if (config.dateFields?.includes?.(fieldName) && value) {
    return new Date(value);
  }

  // Handle boolean fields
  if (config.booleanFields?.includes?.(fieldName) && value) {
    return value.toLowerCase() === "true";
  }

  // Handle numeric fields
  if (config.numericFields?.includes?.(fieldName) && value) {
    return Number(value);
  }

  return value;
}
